import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { capitalize } from '../helpers/common';
import { sortDates, sortNumbers, sortStrings } from '../helpers/filter';

import { DATE_FORMAT } from '../constants/common';
import { BLANK_FILTER_TEXT } from '../constants/livestock';
import { NOTIFICATION_STATUS } from '../constants/notifications';
import { TABLE_CONFIG_COLUMNS } from '../constants/table-configurations';

import { COLUMN_SIZE } from '../components/AppTable/constants';
import Priority from '../components/PriorityTag';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

export const useAlertsColumns = ({ columnFilterOptions, filteredInfo, onClearClick, onSnoozeClick }) => {
  return useMemo(() => {
    return [
      {
        ...commonColumnProperties,
        title: 'Notification ID',
        dataIndex: 'identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.ID,
        filteredValue: filteredInfo.identifier || null,
        searchable: true,
        width: COLUMN_SIZE.SM,
        sorter: (a, b) => sortStrings(a.identifier, b.identifier),
        render: (value, record) => <Link to={`/notification/${record.id}`}>{record.identifier}</Link>
      },
      {
        ...commonColumnProperties,
        title: 'Notification Priority',
        dataIndex: 'priority',
        configColumnKey: TABLE_CONFIG_COLUMNS.PRIORITY,
        filteredValue: filteredInfo.priority || null,
        filters: columnFilterOptions.priority,
        width: COLUMN_SIZE.MD,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.priority : record?.priority === value),
        render: (value, record) => <Priority priority={record.priority} isClear={record.cleared_at}></Priority>,
        sorter: (a, b) => sortNumbers(a.priority_index, b.priority_index)
      },
      {
        ...commonColumnProperties,
        title: 'Notification Type',
        dataIndex: 'type',
        configColumnKey: TABLE_CONFIG_COLUMNS.TYPE,
        filteredValue: filteredInfo.type || null,
        filters: columnFilterOptions.type,
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.type : record?.type === value),
        render: value => capitalize(value),
        sorter: (a, b) => sortStrings(a.type, b.type)
      },
      {
        ...commonColumnProperties,
        title: 'Notification Rule',
        dataIndex: 'ruleName',
        configColumnKey: TABLE_CONFIG_COLUMNS.RULE,
        filteredValue: filteredInfo.ruleName || null,
        searchable: true,
        width: COLUMN_SIZE.XXL,
        onFilter: (value, record) => record.rule?.name?.toLowerCase().includes(value.toLowerCase()),
        render: (value, record) => record?.rule?.name,
        sorter: (a, b) => sortStrings(a?.rule?.name, b?.rule?.name)
      },
      {
        ...commonColumnProperties,
        title: 'Livestock Count',
        dataIndex: 'animals',
        configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_COUNT,
        filteredValue: filteredInfo.animals || null,
        searchable: true,
        width: COLUMN_SIZE.SM,
        onFilter: (value, record) => record.animals?.length === +value,
        render: (value, record) => record.animals?.length ?? 0,
        sorter: (a, b) => sortNumbers(a?.animals?.length, b?.animals?.length)
      },
      {
        ...commonColumnProperties,
        title: 'Notification',
        dataIndex: 'message',
        configColumnKey: TABLE_CONFIG_COLUMNS.MESSAGE,
        filteredValue: filteredInfo.message || null,
        searchable: true,
        width: COLUMN_SIZE.XXL,
        sorter: (a, b) => sortStrings(a.message, b.message)
      },
      {
        ...commonColumnProperties,
        title: 'Notification Trigger Date',
        dataIndex: 'created_at',
        configColumnKey: TABLE_CONFIG_COLUMNS.NOTIFICATION_TRIGGER_DATE,
        filteredValue: filteredInfo.created_at || null,
        searchable: true,
        width: COLUMN_SIZE.XL,
        render: (value, record) => dayjs(record.created_at).format(DATE_FORMAT.DATETIME),
        sorter: (a, b) => sortDates(a.created_at, b.created_at)
      },
      {
        ...commonColumnProperties,
        title: 'Received',
        dataIndex: 'age',
        configColumnKey: TABLE_CONFIG_COLUMNS.AGE,
        filteredValue: filteredInfo.age || null,
        searchable: true,
        width: COLUMN_SIZE.SM,
        onFilter: (value, record) => {
          const text = dayjs.duration(dayjs().diff(dayjs(record.created_at))).humanize() + ' ago';
          return text.includes(value);
        },
        render: (value, record) => dayjs.duration(dayjs().diff(dayjs(record.created_at))).humanize() + ' ago',
        sorter: (a, b) => sortDates(a.created_at, b.created_at)
      },
      {
        ...commonColumnProperties,
        title: 'Status',
        dataIndex: 'notificationStat',
        configColumnKey: TABLE_CONFIG_COLUMNS.STATUS,
        filteredValue: filteredInfo.notificationStat || null,
        filters: columnFilterOptions.status?.map(option => ({
          ...option,
          text: NOTIFICATION_STATUS[option.text.toLowerCase()]
        })),
        width: COLUMN_SIZE.SM,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.status : record?.status === value),
        render: (value, record) => NOTIFICATION_STATUS[record.status],
        sorter: (a, b) => {
          const strA = NOTIFICATION_STATUS[a.status];
          const strB = NOTIFICATION_STATUS[b.status];

          return sortStrings(strA, strB);
        }
      },
      {
        title: 'Snooze',
        dataIndex: 'snooze',
        configColumnKey: TABLE_CONFIG_COLUMNS.SNOOZE,
        filteredValue: filteredInfo.snooze || null,
        align: 'center',
        ellipsis: true,
        noPadding: true,
        width: COLUMN_SIZE.XS,
        render: (value, record) => (
          <i onClick={() => onSnoozeClick(record.id)} className={'not-action mdi mdi-alarm ' + record.status}></i>
        )
      },
      {
        title: 'Clear',
        dataIndex: 'clear',
        configColumnKey: TABLE_CONFIG_COLUMNS.CLEAR,
        filteredValue: filteredInfo.clear || null,
        align: 'center',
        ellipsis: true,
        noPadding: true,
        width: COLUMN_SIZE.XS,
        render: (value, record) => (
          <i
            onClick={() => onClearClick(record.id)}
            className={'not-action clear mdi mdi-check-circle-outline ' + !!record.cleared_at}></i>
        )
      }
    ];
  }, [columnFilterOptions, filteredInfo, onClearClick, onSnoozeClick]);
};
